// src/app/layout/app-layout.component.ts
import { Component, inject, signal, effect, HostListener, ElementRef, ViewChild, ComponentRef, ViewContainerRef, Type } from '@angular/core';
import { NgComponentOutlet } from '@angular/common';
import { RouterLink, RouterLinkActive, RouterOutlet, Router, NavigationEnd } from '@angular/router';
import { CommonModule } from '@angular/common';
import { MenuItem } from '@core/layout/models/menu-item.interface';
import { MenuService } from '@core/services/menu.service';
import { filter } from 'rxjs/operators';
import { AuthService } from '@core/auth/services/auth.service';
import { PageHeaderService, ActionButton } from '@core/layout/services/page-header.service';
import { NotificationService } from '@core/services/notification.service';
import { IconComponent } from '@shared/components/icon/icon.component';
import { AppSwitcherComponent } from '@shared/components/app-switcher/app-switcher.component';

@Component({
  selector: 'app-layout',
  standalone: true,
  imports: [CommonModule, RouterLink, RouterLinkActive, RouterOutlet, IconComponent, AppSwitcherComponent, NgComponentOutlet],
  template: `
    <div class="min-h-screen flex">
      <!-- Sidebar -->
      <aside [class.collapsed]="isMenuCollapsed()" class="sidebar-container">
        <div class="p-3 border-b border-slate-700 flex justify-center items-center">
          <h1 class="text-lg font-semibold mr-auto" [class.hidden]="isMenuCollapsed()">{{ menuService.getAppName() }}</h1>
          <button (click)="toggleMenu()" class="text-white hover:text-slate-300 w-8 h-8 flex items-center justify-center">
            <app-icon [name]="isMenuCollapsed() ? 'menu' : 'menu-open'"></app-icon>
          </button>
        </div>

        <!-- Menu Items -->
        <nav class="flex-1">
          <ul class="space-y-1 mt-2">
            <ng-container *ngFor="let item of menuItems()">
              <li>
                <a [routerLink]="item.children ? null : item.path"
                   (click)="item.children && toggleSubmenu(item)"
                   routerLinkActive="bg-slate-800 text-white"
                   class="menu-item"
                   [class.has-children]="item.children?.length"
                   [class.active]="this.router.isActive(item.path, false)">
                  <app-icon [name]="item.icon || 'circle'" [size]="'base'"></app-icon>
                  <span class="menu-label">{{ item.label }}</span>
                  <app-icon
                    *ngIf="item.children?.length"
                    name="chevron-down"
                    [size]="'base'"
                    class="ml-auto submenu-icon"
                    [style.transform]="isSubmenuOpen(item) ? 'rotate(180deg)' : 'rotate(0)'">
                  </app-icon>
                </a>
                <!-- Submenu items -->
                <ul *ngIf="item.children?.length && isSubmenuOpen(item)"
                    class="submenu-container"
                    [class.collapsed]="isMenuCollapsed()">
                  <li *ngFor="let child of item.children">
                    <a [routerLink]="child.path"
                       routerLinkActive="bg-slate-800 text-white"
                       class="submenu-item"
                       [class.active]="this.router.isActive(child.path, false)">
                      <app-icon [name]="child.icon || 'subdirectory-arrow-right'" [size]="'base'"></app-icon>
                      <span class="menu-label">{{ child.label }}</span>
                    </a>
                  </li>
                </ul>
              </li>
            </ng-container>
          </ul>
        </nav>

        <!-- Company Switcher (only in facturatie app) -->
        <ng-content select="[company-switcher]">
          <!-- Fallback: render company switcher here if provided by facturatie app -->
        </ng-content>

        <!-- App Switcher -->
        <div class="p-2 border-t border-slate-700">
          <app-app-switcher [isMenuCollapsed]="isMenuCollapsed()"></app-app-switcher>
        </div>

        <!-- Logout Button -->
        <div class="p-2 border-t border-slate-700">
          <button
            (click)="logout()"
            class="menu-item w-full flex items-center"
          >
            <app-icon name="logout" [size]="'base'"></app-icon>
            <span class="menu-label">Uitloggen</span>
          </button>
        </div>
      </aside>

      <!-- Main Content -->
      <main class="flex-1 bg-slate-50 flex flex-col h-screen overflow-hidden">
        <div class="p-4 flex flex-col flex-1 overflow-hidden">
          <!-- Header -->
          <header class="bg-white shadow-sm rounded px-4 py-3 mb-4 flex-shrink-0 flex justify-between items-center">
            <div class="flex items-center gap-3">
              <button *ngIf="pageHeader.showBackButton()"
                      (click)="goBack()"
                      class="text-slate-600 hover:text-slate-800 transition-colors flex items-center gap-1">
                <app-icon name="arrow-back" [size]="'base'"></app-icon>
                <!-- <span class="text-sm">Terug</span> -->
              </button>
              <h2 class="text-xl font-medium text-slate-800">{{ pageHeader.title() }}</h2>
            </div>

            <!-- Updated action buttons section -->
            <div class="flex gap-2">
              <div *ngFor="let action of pageHeader.actions()" class="relative">
                <button
                  (click)="action.onClick ? action.onClick() : toggleSubMenu(action)"
                  [disabled]="action.disabled"
                  [class]="getButtonClass(action)"
                  class="action-button px-3 py-2 rounded text-sm transition-colors flex items-center gap-1">
                  <app-icon [name]="action.icon || 'circle'" [size]="'base'"></app-icon>
                  <span [class.hidden-label]="action.hideLabel">{{ action.label }}</span>
                  <span *ngIf="action.subActions?.length" class="material-icons text-sm">expand_more</span>
                </button>

                <!-- Submenu -->
                <div *ngIf="action.subActions?.length && isSubMenuOpen(action)"
                     class="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-slate-200 py-1 min-w-[200px] z-50">
                  <button *ngFor="let subAction of action.subActions"
                          (click)="onSubActionClick(subAction, action)"
                          [disabled]="subAction.disabled"
                          class="w-full px-4 py-2 text-left text-sm hover:bg-slate-50 flex items-center gap-2">
                    <app-icon [name]="subAction.icon || 'circle'" [size]="'base'"></app-icon>
                    {{ subAction.label }}
                  </button>
                </div>
              </div>
            </div>
          </header>

          <!-- Content -->
          <div class="flex-1 overflow-auto">
            <router-outlet></router-outlet>
          </div>
        </div>
      </main>
    </div>
  `,
  styles: [`
    .sidebar-container {
      @apply w-56 bg-secondary-800 text-white flex flex-col transition-all duration-300;
    }

    .sidebar-container.collapsed {
      @apply w-16;
    }

    .menu-item {
      @apply flex gap-3 px-4 py-3 text-sm text-secondary-300 hover:bg-secondary-700/50 transition-colors rounded-lg mx-2;
    }

    .collapsed .menu-item {
      @apply px-2 mx-1 justify-center;
    }

    .collapsed .menu-label {
      @apply hidden;
    }

    .collapsed .menu-item:hover {
      @apply relative;
    }

    .collapsed .menu-item:hover .menu-label {
      @apply block absolute left-full ml-2 bg-secondary-700 px-3 py-2 rounded whitespace-nowrap z-50;
    }

    .action-button {
      @apply relative;
    }

    .hidden-label {
      @apply hidden;
    }

    .action-button:hover .hidden-label {
      @apply block absolute top-full mt-1 left-1/2 -translate-x-1/2
             bg-secondary-800 text-white px-3 py-2 rounded whitespace-nowrap z-50;
    }

    /* Optioneel: voeg een klein driehoekje toe boven het label */
    .action-button:hover .hidden-label::before {
      content: '';
      @apply absolute -top-2 left-1/2 -translate-x-1/2
             border-x-8 border-x-transparent
             border-b-8 border-b-secondary-800;
    }

    .collapsed .menu-item span.material-icons {
      @apply flex items-center justify-center;
    }

    .submenu-overlay {
      @apply fixed inset-0 z-40;
    }

    .menu-item.has-children {
      @apply cursor-pointer;
    }

    .submenu-container {
      @apply ml-4 space-y-1 mt-1;
    }

    .collapsed .submenu-container {
      @apply ml-0;
    }

    .submenu-item {
      @apply flex gap-3 px-4 py-2 text-sm text-secondary-300 hover:bg-secondary-700/50 transition-colors rounded-lg mx-2;
    }

    .collapsed .submenu-item {
      @apply px-2 mx-1 justify-center;
    }

    .submenu-icon {
      @apply transition-transform;
    }

    .collapsed .submenu-icon {
      @apply hidden;
    }
  `]
})
export class AppLayoutComponent {
  protected menuService = inject(MenuService);
  protected router = inject(Router);
  protected authService = inject(AuthService);
  protected pageHeader = inject(PageHeaderService);
  protected notificationService = inject(NotificationService);

  menuItems = signal<MenuItem[]>([]);
  isMenuCollapsed = signal<boolean>(
    JSON.parse(localStorage.getItem('menuCollapsed') || 'false')
  );

  private openSubmenus = new Set<MenuItem>();
  private openActionSubMenus = new Set<ActionButton>();
  companySwitcherComponent: Type<any> | null = null;

  // Voeg click handler toe om submenu's te sluiten bij klik buiten
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.openSubmenus.clear();
      this.openActionSubMenus.clear();
    }
  }

  constructor(
    private elementRef: ElementRef,
  ) {
    // Haal menu items op
    this.menuService.getMenuItems().subscribe(items => {
      this.menuItems.set(items);
    });

    // Luister naar route wijzigingen
    effect(() => {
      this.router.events.pipe(
        filter(event => event instanceof NavigationEnd)
      ).subscribe((event) => {
        if (event instanceof NavigationEnd) {
          //this.updatePageTitle(event.url);
        }
      });
    });

    // Company switcher will be loaded dynamically by facturatie app
    // This is handled via facturatie-specific wrapper
  }

  logout(): void {
    this.authService.logout();
    this.router.navigate(['/login']);
  }

  getButtonClass(action: ActionButton): string {
    const baseClass = 'transition-colors flex items-center gap-1';

    switch (action.variant) {
      case 'secondary':
        return `${baseClass} bg-secondary-100 text-secondary-700 hover:bg-secondary-200`;
      case 'danger':
        return `${baseClass} bg-danger-500 text-white hover:bg-danger-600`;
      case 'primary':
      default:
        return `${baseClass} bg-primary-500 text-white hover:bg-primary-600`;
    }
  }

  toggleMenu(): void {
    const newState = !this.isMenuCollapsed();
    this.isMenuCollapsed.set(newState);
    localStorage.setItem('menuCollapsed', JSON.stringify(newState));
  }

  goBack(): void {
    if (this.pageHeader.backTo()) {
      this.router.navigate([this.pageHeader.backTo()]);
    } else {
      this.router.navigate(['..']);
    }
  }

  toggleSubMenu(action: ActionButton) {
    if (this.isSubMenuOpen(action)) {
      this.openActionSubMenus.delete(action);
    } else {
      this.openActionSubMenus.clear(); // Sluit andere menu's
      this.openActionSubMenus.add(action);
    }
  }

  isSubMenuOpen(action: ActionButton): boolean {
    return this.openActionSubMenus.has(action);
  }

  onSubActionClick(subAction: ActionButton, parentAction: ActionButton) {
    if (subAction.onClick) {
      subAction.onClick();
    }
    this.openActionSubMenus.delete(parentAction);
  }

  toggleSubmenu(item: MenuItem): void {
    if (this.isSubmenuOpen(item)) {
      this.openSubmenus.delete(item);
    } else {
      this.openSubmenus.add(item);
    }
  }

  isSubmenuOpen(item: MenuItem): boolean {
    return this.openSubmenus.has(item);
  }

  /**
   * Check if we're running in the facturatie app
   */
  isFacturatieApp(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }
    
    const hostname = window.location.hostname;
    const port = window.location.port;
    const isDev = hostname === 'localhost' || hostname === '127.0.0.1';
    
    if (isDev) {
      const portNum = port ? parseInt(port, 10) : (window.location.protocol === 'https:' ? 443 : 80);
      return portNum === 4203; // facturatie port
    } else {
      return hostname.includes('facturatie');
    }
  }
}
